import { useId, useState, type CSSProperties } from 'react';
import { Modal } from '../../components/Modal';
import type { SlideOverlayStyle, SlideThemeCatalogRead, SlideThemeTokens } from '../../types/api';
import {
  PANEL_FALLBACK,
  SLIDE_TEMPLATES,
  type SlideTemplateConfig,
} from '../review/slideTemplates';

/**
 * Stand-in for a token the renderer resolves from the brand palette.
 *
 * A catalogue entry may leave `accent` or `panel_fill` null, which means "take
 * it from this brand" — the swatch cannot know that colour without rendering
 * the deck, so it shows the machine blue and the card says brand-derived.
 */
const BRAND_DERIVED_ACCENT = '#3a5a78';

/** What the band treatment is called on a card, in the user's words. */
const OVERLAY_LABEL: Readonly<Record<SlideOverlayStyle, string>> = {
  faded: 'Soft scrim',
  frosted: 'Frosted band',
  clear: 'No band',
  duotone: 'Duotone',
};

/** Layout names come off the wire as plain strings, so an unknown one prints itself. */
function layoutLabel(value: string): string {
  const templates: Record<string, SlideTemplateConfig | undefined> = SLIDE_TEMPLATES;
  return templates[value]?.label ?? value.replace(/_/g, ' ');
}

/** `#rrggbb` plus an alpha 0..1, for the card fill the renderer draws at alpha. */
function withAlpha(color: string, alpha: number): string {
  const clamped = Math.min(Math.max(alpha, 0), 1);
  const channel = Math.round(clamped * 255)
    .toString(16)
    .padStart(2, '0');
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}${channel}` : color;
}

interface ThemeSwatchProps {
  tokens: SlideThemeTokens;
  /** The lightbox draws the same swatch bigger; nothing else changes. */
  large?: boolean;
}

/**
 * A slide-shaped approximation of a theme, drawn from its tokens alone.
 *
 * Shown when the catalogue has no rendered `preview_url`. It is deliberately
 * abstract — a panel, the band treatment, the card and an accent rule — rather
 * than a fake slide with fake words: the honest thing to promise is the palette
 * and the furniture, not a layout the design agent has not chosen yet.
 */
export function ThemeSwatch({ tokens, large = false }: ThemeSwatchProps) {
  const panel = tokens.panel_fill ?? PANEL_FALLBACK;
  const accent = tokens.accent ?? BRAND_DERIVED_ACCENT;
  const cardStyle: CSSProperties = {
    background: withAlpha(tokens.card_fill ?? '#f7f5f0', tokens.card_fill_alpha),
    borderRadius: `${Math.round(tokens.card_radius / 6)}px`,
    borderWidth: `${Math.min(tokens.card_border_width, 4)}px`,
    borderColor: tokens.card_border_color ?? 'transparent',
    borderStyle: tokens.card_border_width > 0 ? 'solid' : 'none',
    boxShadow: tokens.card_shadow ? '0 4px 10px rgba(0, 0, 0, 0.35)' : 'none',
  };

  return (
    <span
      className={large ? 'theme-swatch theme-swatch--large' : 'theme-swatch'}
      style={{ background: panel }}
      aria-hidden="true"
    >
      <span className={`theme-swatch__band theme-swatch__band--${tokens.overlay_style}`} />
      <span
        className={
          tokens.card_enabled ? 'theme-swatch__card theme-swatch__card--on' : 'theme-swatch__card'
        }
        style={tokens.card_enabled ? cardStyle : undefined}
      >
        <span className="theme-swatch__rule" style={{ background: accent }} />
        <span className="theme-swatch__line" />
        <span className="theme-swatch__line theme-swatch__line--short" />
      </span>
    </span>
  );
}

/** The preview picture when one exists, the token swatch when it does not. */
export function ThemePreview({
  theme,
  large = false,
}: {
  theme: SlideThemeCatalogRead;
  large?: boolean;
}) {
  if (theme.preview_url === null) {
    return <ThemeSwatch tokens={theme.tokens} large={large} />;
  }
  return (
    <img
      className={large ? 'theme-preview theme-preview--large' : 'theme-preview'}
      src={theme.preview_url}
      alt={`${theme.name} sample slide`}
      loading="lazy"
    />
  );
}

export interface ThemeGalleryProps {
  themes: readonly SlideThemeCatalogRead[];
  /** The chosen look. Null is the auto card when `allowAuto`, nothing otherwise. */
  selectedId: string | null;
  /** Null arrives only from the auto card. */
  onSelect: (themeId: string | null) => void;
  /** Marked with a tick wherever it appears in the grid. */
  brandDefaultId?: string | null;
  /** Omit to hide the per-card default action entirely. */
  onSetBrandDefault?: (theme: SlideThemeCatalogRead) => void;
  /** Tighter grid for the review screen's dialog. */
  compact?: boolean;
  /** Offers "Auto (brand default)" as the first card, selected when `selectedId` is null. */
  allowAuto?: boolean;
  /** How prominent the per-card default action is; primary where it is the point of the screen. */
  setDefaultVariant?: 'ghost' | 'primary';
  disabled?: boolean;
}

/**
 * The look gallery — one grid of themes, shared by the three screens that pick
 * one: the brief, the knowledge base's Looks tab and Review Step 2.
 *
 * A card is a real radio, so the grid is one tab stop and arrow keys move
 * through it. The two per-card actions sit outside the label rather than inside
 * it, because a button nested in a label is a button that also toggles the
 * radio.
 *
 * The gallery owns no data: it neither fetches the catalogue nor writes the
 * brand default. Selecting is the caller's to interpret — a brief stores it, the
 * knowledge base saves it, the review screen re-renders a deck with it.
 */
export function ThemeGallery({
  themes,
  selectedId,
  onSelect,
  brandDefaultId = null,
  onSetBrandDefault,
  compact = false,
  allowAuto = false,
  setDefaultVariant = 'ghost',
  disabled = false,
}: ThemeGalleryProps) {
  const groupName = useId();
  const [zoomed, setZoomed] = useState<SlideThemeCatalogRead | null>(null);

  const defaultTheme = themes.find((theme) => theme.id === brandDefaultId) ?? null;

  return (
    <>
      <ul className={compact ? 'theme-grid theme-grid--compact' : 'theme-grid'}>
        {allowAuto ? (
          <li className={selectedId === null ? 'theme-card theme-card--selected' : 'theme-card'}>
            <label className="theme-card__label">
              <input
                className="visually-hidden"
                type="radio"
                name={groupName}
                value=""
                checked={selectedId === null}
                disabled={disabled}
                onChange={() => onSelect(null)}
              />
              <span className="theme-card__auto" aria-hidden="true">
                Auto
              </span>
              <span className="theme-card__name">Auto (brand default)</span>
              <span className="theme-card__description">
                {defaultTheme
                  ? `Renders in ${defaultTheme.name}, your brand default.`
                  : 'Uses your brand default style, or the classic one until you set it.'}
              </span>
            </label>
          </li>
        ) : null}

        {themes.map((theme) => {
          const isSelected = theme.id === selectedId;
          const isBrandDefault = theme.id === brandDefaultId;

          return (
            <li
              className={isSelected ? 'theme-card theme-card--selected' : 'theme-card'}
              key={theme.id}
            >
              <label className="theme-card__label">
                <input
                  className="visually-hidden"
                  type="radio"
                  name={groupName}
                  value={theme.id}
                  checked={isSelected}
                  disabled={disabled}
                  onChange={() => onSelect(theme.id)}
                />
                <ThemePreview theme={theme} />
                <span className="theme-card__name">
                  {theme.name}
                  {isBrandDefault ? (
                    <span className="theme-card__badge">✓ Brand default</span>
                  ) : null}
                </span>
                <span className="theme-card__description">{theme.description}</span>
                {compact || theme.layout_preferences.length === 0 ? null : (
                  <span className="theme-card__layouts">
                    Favours {theme.layout_preferences.map(layoutLabel).join(', ').toLowerCase()}
                  </span>
                )}
              </label>

              <div className="theme-card__actions">
                <button
                  className="button button--ghost theme-card__zoom"
                  type="button"
                  onClick={() => setZoomed(theme)}
                >
                  Zoom
                </button>
                {onSetBrandDefault && !isBrandDefault ? (
                  <button
                    className={
                      setDefaultVariant === 'primary'
                        ? 'button button--primary theme-card__default'
                        : 'button button--ghost theme-card__default'
                    }
                    type="button"
                    disabled={disabled}
                    onClick={() => onSetBrandDefault(theme)}
                  >
                    Set as brand default
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      {zoomed ? (
        <Modal title={zoomed.name} kind="look" onDismiss={() => setZoomed(null)}>
          <div className="theme-zoom">
            <ThemePreview theme={zoomed} large />
            <p className="theme-zoom__description">{zoomed.description}</p>

            <div className="theme-zoom__actions">
              <button
                className="button button--primary"
                type="button"
                disabled={disabled}
                onClick={() => {
                  onSelect(zoomed.id);
                  setZoomed(null);
                }}
              >
                Use this style
              </button>
              {zoomed.preview_url === null ? (
                <p className="theme-zoom__note">
                  No sample has been rendered for this style yet — the panel above is drawn from its
                  own tokens, not from a slide.
                </p>
              ) : null}
            </div>

            <dl className="spec-sheet">
              <div className="spec-sheet__row">
                <dt className="spec-sheet__term">Band</dt>
                <dd className="spec-sheet__value">{OVERLAY_LABEL[zoomed.tokens.overlay_style]}</dd>
              </div>
              <div className="spec-sheet__row">
                <dt className="spec-sheet__term">Text card</dt>
                <dd className="spec-sheet__value">{zoomed.tokens.card_enabled ? 'On' : 'Off'}</dd>
              </div>
              <div className="spec-sheet__row">
                <dt className="spec-sheet__term">Accent</dt>
                <dd className="spec-sheet__value">
                  {zoomed.tokens.accent ?? 'From your brand palette'}
                </dd>
              </div>
              <div className="spec-sheet__row">
                <dt className="spec-sheet__term">Layouts</dt>
                <dd className="spec-sheet__value">
                  {zoomed.layout_preferences.length === 0
                    ? 'Any, we pick'
                    : zoomed.layout_preferences.map(layoutLabel).join(', ')}
                </dd>
              </div>
            </dl>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
